import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Code2, Search, BookOpen, Lightbulb, Info } from "lucide-react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { SilaeVariableSearch } from "@/components/dashboard/SilaeVariableSearch";

import type {
  SilaeVariable,
  SilaeVariableType,
  SilaeVariableCategory,
} from "@/types/silae";
import { useAppSelector } from "@/hooks/redux";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Mail } from "lucide-react";
import { HelpCircle } from "lucide-react";

export const VariablesTab: React.FC = () => {
  const { t } = useTranslation();
  const [selectedVariable, setSelectedVariable] =
    useState<SilaeVariable | null>(null);
  const [isContactSupportOpen, setIsContactSupportOpen] = React.useState(false);
  const { user } = useAppSelector((state) => state.auth);

  const handleVariableSelect = (variable: SilaeVariable) => {
    setSelectedVariable(variable);
  };

  const getTypeBadgeColor = (type: SilaeVariableType) => {
    switch (type) {
      case "date":
        return "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300";
      case "int":
        return "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300";
      case "double":
        return "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300";
      case "boolean":
        return "bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300";
      case "function":
        return "bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-300";
      default:
        return "bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-300";
    }
  };

  const getCategoryBadgeColor = (category: SilaeVariableCategory) => {
    switch (category) {
      case "bulletin":
        return "bg-indigo-100 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-300";
      case "salary":
        return "bg-emerald-100 text-emerald-800 dark:bg-emerald-900 dark:text-emerald-300";
      case "employee":
        return "bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-300";
      case "mtpart":
        return "bg-pink-100 text-pink-800 dark:bg-pink-900 dark:text-pink-300";
      case "functions":
        return "bg-cyan-100 text-cyan-800 dark:bg-cyan-900 dark:text-cyan-300";
      default:
        return "bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-300";
    }
  };

  const handleContactSupport = () => {
    const email = import.meta.env.VITE_SUPPORT_EMAIL;
    const subject = encodeURIComponent(
      `Support Variables SILAE - ${import.meta.env.VITE_APP_NAME}`,
    );
    const currentDate = new Date().toLocaleDateString("fr-FR");
    const currentTime = new Date().toLocaleTimeString("fr-FR");

    const body = encodeURIComponent(
      `Bonjour l'équipe Support,

J'ai besoin d'assistance concernant les Variables SILAE :
[Veuillez décrire votre question ou problème ici]


TYPE DE DEMANDE :
□ Question sur une variable SILAE spécifique
□ Problème avec la recherche de variables
□ Documentation d'une variable manquante ou incorrecte
□ Demande d'ajout de nouvelles variables
□ Problème technique avec l'interface de recherche
□ Question sur l'utilisation d'une variable dans un calcul
□ Autre : _______________


VARIABLE CONCERNÉE (si applicable) :
- Code de la variable : ${selectedVariable?.code || "[À préciser]"}
- Type de variable : ${selectedVariable?.type || "[À préciser]"}
- Catégorie : ${selectedVariable?.category || "[À préciser]"}


CONTEXTE D'UTILISATION :
- Calcul de paie concerné : 
- Période de paie : 
- Entreprise/Convention : 


DÉTAILS TECHNIQUES :
- Navigateur utilisé : ${
        navigator.userAgent.includes("Chrome")
          ? "Chrome"
          : navigator.userAgent.includes("Firefox")
            ? "Firefox"
            : navigator.userAgent.includes("Safari")
              ? "Safari"
              : navigator.userAgent.includes("Edge")
                ? "Edge"
                : "Autre"
      }
- Système d'exploitation : ${navigator.platform || "Non déterminé"}
- Date et heure : ${currentDate} à ${currentTime}
- Page : Variables SILAE (${window.location.href})


INFORMATIONS UTILISATEUR :
- Email : ${user?.email || "Non renseigné"}
- Nom complet : ${user?.firstName || ""} ${user?.lastName || ""}
- Plan d'abonnement : ${user?.subscriptionPlan || "Gratuit"}
- Statut d'abonnement : ${user?.subscriptionStatus || "Non défini"}


QUESTION OU PROBLÈME DÉTAILLÉ :
[Décrivez précisément votre question ou le problème rencontré]


EXEMPLE D'UTILISATION SOUHAITÉ (si applicable) :
[Exemple de calcul ou d'utilisation de la variable]


Merci pour votre aide avec les variables SILAE.

Cordialement,
${user?.firstName || ""} ${user?.lastName || ""}`,
    );

    // Create mailto link
    const mailtoLink = `mailto:${email}?subject=${subject}&body=${body}`;

    // Open email client
    window.location.href = mailtoLink;
  };

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex flex-col space-y-2">
        <div className="flex items-center space-x-2">
          <h1 className="text-2xl font-bold tracking-tight">
            {t("dashboard.variables.title")}
          </h1>
        </div>
        <p className="text-muted-foreground">
          {t("dashboard.variables.description")}
        </p>
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        {/* Search Section */}
        <div className="lg:col-span-2 space-y-6">
          {/* Search Card */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <Search className="h-5 w-5" />
                <span>{t("dashboard.variables.searchTitle")}</span>
              </CardTitle>
              <CardDescription>
                {t("dashboard.variables.searchDescription")}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <SilaeVariableSearch
                onVariableSelect={handleVariableSelect}
                placeholder={t("dashboard.variables.searchPlaceholder")}
                className="w-full"
              />
            </CardContent>
          </Card>

          {/* Selected Variable Details */}
          {selectedVariable && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <Code2 className="h-5 w-5" />
                  <span>{t("dashboard.variables.detailsTitle")}</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center space-x-2">
                  <span className="font-mono text-lg font-semibold">
                    {selectedVariable.code}
                  </span>
                  <Badge className={getTypeBadgeColor(selectedVariable.type)}>
                    {selectedVariable.type.toUpperCase()}
                  </Badge>
                  <Badge
                    className={getCategoryBadgeColor(selectedVariable.category)}
                  >
                    {selectedVariable.category}
                  </Badge>
                  {selectedVariable.isFrequentlyUsed && (
                    <Badge variant="secondary">
                      <Lightbulb className="h-3 w-3 mr-1" />
                      {t("dashboard.variables.frequent")}
                    </Badge>
                  )}
                </div>

                <div>
                  <h4 className="font-medium mb-2">
                    {t("dashboard.variables.description_label")}
                  </h4>
                  <p className="text-sm text-muted-foreground">
                    {selectedVariable.definition}
                  </p>
                </div>

                {selectedVariable.example && (
                  <div>
                    <h4 className="font-medium mb-2">
                      {t("dashboard.variables.exampleTitle")}
                    </h4>
                    <div className="bg-muted p-3 rounded-md">
                      <code className="text-sm font-mono">
                        {selectedVariable.example}
                      </code>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Quick Tips */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <Lightbulb className="h-5 w-5" />
                <span>{t("dashboard.variables.tipsTitle")}</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex items-start space-x-2">
                <div className="w-2 h-2 bg-primary rounded-full mt-2 flex-shrink-0" />
                <p className="text-sm text-muted-foreground">
                  {t("dashboard.variables.tipsSearch")}
                </p>
              </div>
              <div className="flex items-start space-x-2">
                <div className="w-2 h-2 bg-primary rounded-full mt-2 flex-shrink-0" />
                <p className="text-sm text-muted-foreground">
                  {t("dashboard.variables.tipsNavigation")}
                </p>
              </div>
              <div className="flex items-start space-x-2">
                <div className="w-2 h-2 bg-primary rounded-full mt-2 flex-shrink-0" />
                <p className="text-sm text-muted-foreground">
                  {t("dashboard.variables.tipsFrequent")}
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Categories Info */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <BookOpen className="h-5 w-5" />
                <span>{t("dashboard.variables.categoriesTitle")}</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex items-center space-x-2">
                <Badge className="bg-indigo-100 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-300">
                  Bulletin
                </Badge>
                <span className="text-sm text-muted-foreground">
                  {t("dashboard.variables.bulletinCategory")}
                </span>
              </div>
              <div className="flex items-center space-x-2">
                <Badge className="bg-emerald-100 text-emerald-800 dark:bg-emerald-900 dark:text-emerald-300">
                  Salarié
                </Badge>
                <span className="text-sm text-muted-foreground">
                  {t("dashboard.variables.salaryCategory")}
                </span>
              </div>
              <div className="flex items-center space-x-2">
                <Badge className="bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-300">
                  Emploi
                </Badge>
                <span className="text-sm text-muted-foreground">
                  {t("dashboard.variables.employeeCategory")}
                </span>
              </div>
              <div className="flex items-center space-x-2">
                <Badge className="bg-pink-100 text-pink-800 dark:bg-pink-900 dark:text-pink-300">
                  Montants
                </Badge>
                <span className="text-sm text-muted-foreground">
                  {t("dashboard.variables.mtpartCategory")}
                </span>
              </div>
              <div className="flex items-center space-x-2">
                <Badge className="bg-cyan-100 text-cyan-800 dark:bg-cyan-900 dark:text-cyan-300">
                  Fonctions
                </Badge>
                <span className="text-sm text-muted-foreground">
                  {t("dashboard.variables.functionsCategory")}
                </span>
              </div>
            </CardContent>
          </Card>

          {/* Help */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <Info className="h-5 w-5" />
                <span>{t("dashboard.variables.helpTitle")}</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground mb-3">
                {t("dashboard.variables.helpDescription")}
              </p>
              {/* Contact Support Dialog */}
              <div className="text-center mt-8">
                <Dialog
                  open={isContactSupportOpen}
                  onOpenChange={setIsContactSupportOpen}
                >
                  <DialogTrigger asChild>
                    <Button variant="outline">
                      <HelpCircle className="h-4 w-4 mr-2" />
                      Support Variables SILAE
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                      <DialogTitle className="flex items-center gap-2">
                        <HelpCircle className="h-5 w-5" />
                        Support Variables SILAE
                      </DialogTitle>
                      <DialogDescription>
                        Besoin d'aide avec une variable SILAE spécifique ? Notre
                        équipe peut vous aider avec l'utilisation, la
                        documentation ou l'intégration des variables dans vos
                        calculs de paie.
                      </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4">
                      <div className="flex flex-col space-y-3">
                        <div className="flex items-center justify-between p-4 border rounded-lg bg-muted/50">
                          <div className="flex items-center gap-3">
                            <Mail className="h-5 w-5 text-muted-foreground" />
                            <div>
                              <p className="font-medium">Email Support</p>
                              <p className="text-sm text-muted-foreground">
                                {import.meta.env.VITE_SUPPORT_EMAIL}
                              </p>
                            </div>
                          </div>
                        </div>

                        <Button
                          onClick={handleContactSupport}
                          className="w-full"
                        >
                          <Mail className="h-4 w-4 mr-2" />
                          Contacter le Support SILAE
                        </Button>

                        <p className="text-xs text-muted-foreground text-center">
                          Un email pré-rempli avec les détails de votre contexte
                          SILAE s'ouvrira dans votre application email.
                        </p>
                      </div>
                    </div>
                  </DialogContent>
                </Dialog>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};
